import React, { ChangeEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Label,
  Link,
  Button,
  InputText,
  Spinner,
  THead,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Paragraph,
  Grid
} from '@increasecard/typed-components';
import styled from 'styled-components';
import { CredRow, Shop } from '@card/types';
import { SmallLink } from '@card/commonlib';

const ErrorMessage = styled(Label)`
  padding: 20px 0 10px;
  color: red;
`;

const Scrollable = styled.div`
  height: 265px;
  overflow: auto;
`;

const Header = styled.div<{ align: string }>`
  text-align: ${(props) => props.align};
  font-weight: 500;
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const TableRegister = styled(Grid)`
  background-color: #f5f5f5;
  :nth-child(2n) {
    background: transparent;
  }
  padding-top: 11px;
  padding-bottom: 13px;
`;

const MainDescription = styled(Paragraph)`
  margin-bottom: 14px;
`;

const HeaderContainer = styled(Grid)`
  margin: 1em 0;
`;

const DeleteLinkContainer = styled.div`
  display: flex;
  align-items: center;
`;

/* eslint-disable @typescript-eslint/no-explicit-any */
type ShopsModalProps = {
  credentialsList?: CredRow[];
  visible: boolean;
  shops: Shop[];
  onCreateShop: () => void;
  onClose: () => void;
  onChangeName: ({ id, name }: any) => any;
  onDestroyShop: ({ id, position }: any) => void;
  onSave: (params: any) => any;
};
/* eslint-enable @typescript-eslint/no-explicit-any */

const getShopsCantBeRemoved = (visible: boolean, credentials?: CredRow[]) => {
  const shopsCantBeRemoved: string[] = [];
  if (visible && credentials) {
    credentials.forEach((credential) => {
      credential.establishments.forEach((establishment) => {
        if (
          establishment.shop_id &&
          establishment.shop_id.length > 0 &&
          !shopsCantBeRemoved.includes(establishment.shop_id)
        )
          shopsCantBeRemoved.push(establishment.shop_id);
      });
    });
  }
  return shopsCantBeRemoved;
};

const MODAL_SIZE = '580';

export const ShopsEditModal = ({
  credentialsList,
  onClose,
  visible,
  shops,
  onCreateShop,
  onChangeName,
  onDestroyShop,
  onSave
}: ShopsModalProps) => {
  const { t } = useTranslation();

  let fetchError = false;
  const shopsCantBeRemoved = useMemo(() => getShopsCantBeRemoved(visible, credentialsList), [
    credentialsList,
    visible
  ]);

  shops.forEach((shop) => (fetchError = fetchError || shop.status === 'FETCH_ERROR'));

  return (
    <Modal
      onCancel={() => {
        return;
      }}
      onClose={onClose}
      onOk={() => {
        return;
      }}
      visible={visible}
      width={MODAL_SIZE}
    >
      <ModalHeader align="left">{t('abm:Shops:ShopsEditModal:title')}</ModalHeader>
      <ModalContent align="left">
        <MainDescription>{t('abm:Shops:ShopsEditModal:description')}</MainDescription>
        <div>
          <THead as="div">
            <HeaderContainer rowGap={2}>
              <Header align="center" className="grid-span-desktop-3 grid-span-mobile-1">
                {t('abm:Shops:ShopsEditModal:numberHeader')}
              </Header>
              <Header align="initial" className="grid-span-desktop-7 grid-span-mobile-1">
                {t('abm:Shops:ShopsEditModal:nameHeader')}
              </Header>
              <div className="grid-span-desktop-2 grid-span-mobile-1" />
            </HeaderContainer>
          </THead>
          <Scrollable>
            {shops.map((shop, index) => {
              const handleChangeName = (e: ChangeEvent<HTMLInputElement>) =>
                onChangeName({ id: shop.id, name: e.target.value });
              const handleDestroyShop = () => onDestroyShop({ id: shop.id, position: index });
              return (
                <TableRegister key={index} rowGap={2}>
                  <div
                    className="grid-span-desktop-3 grid-span-mobile-1"
                    style={{ textAlign: 'center' }}
                  >
                    <p style={{ marginTop: '8px' }}>{`#${index + 1}`}</p>
                  </div>
                  <div className="grid-span-desktop-6 grid-span-mobile-1">
                    <div>
                      <InputText
                        className={shop.status === 'FETCH_ERROR' ? 'inputError' : ''}
                        id={shop.name}
                        label=""
                        labelHidden
                        onChange={handleChangeName}
                        placeholder={shop.name}
                        value={shop.name}
                        width="240px"
                      />
                      {shop.status === 'LOADING' && <Spinner size={20} />}
                    </div>
                  </div>
                  <DeleteLinkContainer className="grid-span-desktop-2 grid-span-mobile-1">
                    <Link
                      as="button"
                      disabled={shopsCantBeRemoved.includes(shop.id)}
                      onClick={handleDestroyShop}
                      variant="alert"
                    >
                      {t(`abm:Shops:ShopsEditModal:delete`)}
                    </Link>
                  </DeleteLinkContainer>
                </TableRegister>
              );
            })}
          </Scrollable>
        </div>
      </ModalContent>
      {fetchError && <ErrorMessage>{t('abm:Shops:ShopsEditModal:errorMessage')}</ErrorMessage>}
      <ModalFooter>
        <Button onClick={() => onSave(shops)}>{t('abm:Shops:ShopsEditModal:save')}</Button>
        <Button buttonType="invisible" onClick={onClose}>
          {t('abm:Shops:ShopsEditModal:cancel')}
        </Button>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            width: '100%'
          }}
        >
          <SmallLink as="button" onClick={onCreateShop}>
            {t('abm:Shops:ShopsEditModal:createShop')}
          </SmallLink>
        </div>
      </ModalFooter>
    </Modal>
  );
};
